/* eslint-disable no-param-reassign */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  bool,
  func,
  shape,
  node,
  string,
  oneOfType,
  arrayOf,
  number
} from 'prop-types';
import { GlobalRemoteEnum } from '@core/types';
import { useTranslation, useEnums } from '@core/hooks';

// devExtreme
import { SelectBox } from 'devextreme-react/select-box';

// components
import {
  Dropzone,
  LabeledItemsGroup,
  Spinner,
  LabeledItem,
  ExtendedHtmlEditor
} from '@core/components';

// hooks
import useGrid from './hooks/useGrid';
import useLangSelect from './hooks/useLangSelect';

// context
import { TemplateContext } from './context/TemplateContext';

export { TemplateContext } from './context/TemplateContext';
export { default as useDraggableValue } from './hooks/useDraggableValue';

export const TemplateCreator = ({
  grid,
  setGridHeight,
  dropzoneClassName,
  editorInstancesForGrid,
  fireSaveHandlerRef,
  maxHtmlHeader,
  setEditorInstancesForGrid,
  showHtmlEditor = true,
  htmlEditorOpts,
  txtEditorOpts,
  gridDataLoaded = true,
  showLangInp = true,
  customInput,
  showSpinner,
  lang,
  onLangChange,
  saveData,
  setSaveData,
  children
}) => {
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [htmlEditorDimensions, setHtmlEditorDimensions] = useState(null);

  const wrapperRef = useRef(null);
  const editorWrapperRef = useRef(null);
  const htmlEditorInstance = useRef(null);
  const textAreaInstance = useRef(null);

  const langs = useEnums(GlobalRemoteEnum.JEZYK_TYP);

  const { t } = useTranslation();

  const [
    onLangSelectChanged,
    getTextVal,
    getHtmlVal,
    setHtmlVal,
    handleRawState
  ] = useLangSelect(
    showRawHtml,
    setShowRawHtml,
    htmlEditorInstance,
    textAreaInstance,
    saveData,
    setSaveData,
    lang,
    onLangChange,
    fireSaveHandlerRef
  );

  const [handleDrop, setUpCaretIndex] = useGrid(
    editorInstancesForGrid || {
      showRawHtml,
      htmlEditorInstance,
      textAreaInstance,
      getTextVal
    }
  );

  useEffect(() => {
    if (setGridHeight) {
      setGridHeight(wrapperRef.current.clientHeight);
    }

    setHtmlEditorDimensions({
      height: editorWrapperRef.current.clientHeight,
      width: editorWrapperRef.current.clientWidth
    });
  }, []);

  useEffect(() => {
    if (setEditorInstancesForGrid) {
      setEditorInstancesForGrid({
        showRawHtml,
        htmlEditorInstance,
        textAreaInstance,
        getTextVal
      });
    }
  }, [showRawHtml]);

  const setInstance = useCallback(
    (ref) => (instance) => {
      ref.current = instance;
    },
    []
  );

  const handleGridDblClick = ({ target: { dataset } }) => {
    const { rowVal } = dataset;

    if (!rowVal) return null;
    setUpCaretIndex();

    const dataTransfer = { getData: () => rowVal };

    handleDrop({ dataTransfer });
  };

  return (
    <TemplateContext.Provider
      value={{ getHtmlVal, setHtmlVal, handleRawState }}
    >
      <div
        className={`template-creator ${grid ? '' : 'template-creator--nogrid'}`}
        ref={wrapperRef}
      >
        <div className="template-creator__editor hide-popup-root">
          <LabeledItemsGroup>
            {customInput || null}
            {showLangInp && (
              <LabeledItem labelText={t('Jezyk')}>
                <SelectBox
                  dataSource={{
                    store: {
                      type: 'array',
                      data: langs,
                      key: 'id'
                    }
                  }}
                  onValueChanged={onLangSelectChanged}
                  value={lang}
                  displayExpr="name"
                  valueExpr="id"
                />
              </LabeledItem>
            )}
          </LabeledItemsGroup>
          {!htmlEditorDimensions || showSpinner ? (
            <div ref={editorWrapperRef} className="spinner-wrapper">
              <Spinner />
            </div>
          ) : (
            <Dropzone
              onDrop={handleDrop}
              onlyFiles={false}
              className={dropzoneClassName}
            >
              {gridDataLoaded && showHtmlEditor && (
                <ExtendedHtmlEditor
                  usingRefInstances
                  wrapperStyle={{ maxWidth: htmlEditorDimensions.width }}
                  maxHtmlHeader={maxHtmlHeader}
                  htmlEditorOpts={htmlEditorOpts}
                  txtEditorOpts={txtEditorOpts}
                  height={!showRawHtml ? htmlEditorDimensions.height : 78}
                  showRawHtml={showRawHtml}
                  setShowRawHtml={setShowRawHtml}
                  htmlEditorInstance={htmlEditorInstance}
                  setHtmlEditorInstance={setInstance(htmlEditorInstance)}
                  textAreaInstance={textAreaInstance}
                  setTextAreaInstance={setInstance(textAreaInstance)}
                />
              )}
              {children}
            </Dropzone>
          )}
        </div>
        {grid && (
          <div
            className="template-creator__grid"
            role="presentation"
            onDoubleClick={handleGridDblClick}
            onDragStart={setUpCaretIndex}
          >
            {grid}
          </div>
        )}
      </div>
    </TemplateContext.Provider>
  );
};

TemplateCreator.propTypes = {
  grid: node,
  customInput: node,
  setGridHeight: func,
  gridDataLoaded: bool,
  maxHtmlHeader: number,
  showSpinner: bool,
  showHtmlEditor: bool,
  lang: string,
  dropzoneClassName: string,
  showLangInp: bool,
  htmlEditorOpts: shape({}),
  txtEditorOpts: shape({}),
  fireSaveHandlerRef: shape({
    current: func
  }),
  editorInstancesForGrid: shape({}),
  children: oneOfType([arrayOf(node), node]),
  onLangChange: func.isRequired,
  setEditorInstancesForGrid: func,
  saveData: oneOfType([bool, shape({})]),
  setSaveData: func.isRequired
};
